import { Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { puedeAccederFichaIngreso, puedeAccederKardexYCaja, esRolTerapeuta } from "../services/permisoService";
import { obtenerUsuario } from "../services/authService";

// El menu ya oculta lo que el usuario no tiene, pero una URL escrita a mano
// llegaria igual a la ventana. Este guard cierra esa via: cada ruta declara el
// acceso que la concede y, si el usuario no lo tiene, se vuelve al inicio.
//
// Con la seguridad desactivada, o sin accesos cargados todavia, no se bloquea
// nada: es el comportamiento permisivo de siempre, para poder trabajar sin dar
// de alta los roles.
//
// El orden importa: "/session" exacto es la ventana general (sesion.ver),
// mientras "/session/:id" y "/session/edit/..." son las del terapeuta.
//
// Tambien importa que las rutas de edicion (/algo/edit) van antes que las
// generales: la busqueda se detiene en la primera que coincide, y "/x/edit/1"
// tambien empieza por "/x". Con las edit despues, la regla de editar nunca
// se aplicaba.
const RUTAS_POR_ACCESO = [
    { ruta: "/session", acceso: "sesion.ver", exacta: true },
    { ruta: "/session/edit", acceso: "sesionTerapeuta.ver" },
    { ruta: "/session/", acceso: "sesionTerapeuta.ver" },
    { ruta: "/sesionterapeuta", acceso: "sesionTerapeuta.ver" },
    { ruta: "/planilla", acceso: "sesion.ver" },

    // Los reportes se listan juntos en su pagina: cualquiera de los accesos deja entrar.
    // Las rutas especificas van antes de "/reporte" exacta.
    { ruta: "/reporte/ficha-participante", acceso: "participante.ver" },
    { ruta: "/reporte/curva-eficacia", acceso: ["terapiaPlanReporte.ver", "terapiaPlan.ver"] },
    { ruta: "/reporte/trimestral", acceso: "terapiaPlanReporte.ver" },
    { ruta: "/reporte/modelo", acceso: "modeloReporte.ver" },
    { ruta: "/reporte/terapia", acceso: "terapiaReporte.ver" },
    { ruta: "/reporte/sesion", acceso: "sesionReporte.ver" },
    { ruta: "/reporte/planterapia", acceso: "terapiaPlanReporte.ver" },
    { ruta: "/reporte", acceso: ["modeloReporte.ver", "terapiaReporte.ver", "sesionReporte.ver", "terapiaPlanReporte.ver", "participante.ver"], exacta: true },

    // Módulos de Kardex / Inventario y Caja Chica
    { ruta: "/inventario/articulos", acceso: "articulo.ver" },
    { ruta: "/inventario/kardex", acceso: "kardex.ver" },
    { ruta: "/inventario/reportes", acceso: ["kardex.reporte", "kardex.ver"] },
    { ruta: "/cajachica/reportes", acceso: ["cajachica.reporte", "cajachica.ver"] },
    { ruta: "/cajachica", acceso: "cajachica.ver" },

    { ruta: "/evaluacion/edit", acceso: "evaluacion.editar" },
    { ruta: "/evaluacion/temas/edit", acceso: "evaluacion.editar" },
    { ruta: "/evaluacion", acceso: "evaluacion.ver" },
    { ruta: "/evaluacion/temas", acceso: "evaluacion.ver" },

    { ruta: "/person", acceso: "persona.ver" },
    { ruta: "/person/edit", acceso: "persona.editar" },
    { ruta: "/member", acceso: "participante.ver" },
    { ruta: "/trabajador/edit", acceso: "trabajador.editar" },
    { ruta: "/trabajador", acceso: "trabajador.ver" },
    { ruta: "/cargo/edit", acceso: "trabajador.editar" },
    { ruta: "/cargo", acceso: "trabajador.ver" },
    { ruta: "/grupocargo/edit", acceso: "trabajador.editar" },
    { ruta: "/grupocargo", acceso: "trabajador.ver" },
    { ruta: "/atencion", acceso: "atencion.ver" },
    { ruta: "/diagnostico", acceso: "diagnostico.ver" },
    { ruta: "/model", acceso: "modelo.ver" },

    { ruta: "/therapyperiod/pago", acceso: "terapiaPeriodoPago.ver" },
    { ruta: "/therapyPeriod/open", acceso: ["terapiaPeriodo.abrir", "terapiaPeriodo.editar"] },
    { ruta: "/therapyperiod/open", acceso: ["terapiaPeriodo.abrir", "terapiaPeriodo.editar"] },
    { ruta: "/therapyPeriod", acceso: "terapiaPeriodo.ver" },
    // El orden importa: "/therapyplanterapist" y "/therapyplan/edit" empiezan
    // por "/therapyplan", asi que tienen que declararse antes o los agarra la
    // entrada general, que pide terapiaPlan.ver.
    { ruta: "/therapyplanterapist/edit", acceso: ["terapiaPlan.ver", "terapiaPlanTerapeuta.ver"] },
    { ruta: "/therapyplanterapist", acceso: "terapiaPlanTerapeuta.ver" },
    { ruta: "/therapyplan/edit", acceso: ["terapiaPlan.ver", "terapiaPlanTerapeuta.ver"] },
    { ruta: "/therapyplan", acceso: "terapiaPlan.ver" },
    { ruta: "/therapy", acceso: "terapia.ver" },
    { ruta: "/period", acceso: "periodo.ver" },
    // Los horarios disponibles los declara el terapeuta: los ve quien
    // registra sesiones.
    { ruta: "/horario-terapeuta", acceso: "sesion.ver" },

    { ruta: "/receipt/pago/anular", acceso: "pago.anular" },
    { ruta: "/receipt/pago/consolidar", acceso: "pago.consolidar" },
    { ruta: "/receipt", acceso: "comprobante.ver" },

    { ruta: "/catalog", acceso: "catalogo.ver" },
    { ruta: "/filial", acceso: "filial.ver" },
    { ruta: "/salon", acceso: "salon.ver" },
    { ruta: "/direccion", acceso: "direccion.ver" },
    { ruta: "/ubigeo", acceso: "ubigeo.ver" },
    { ruta: "/diaferiado", acceso: "diaFeriado.ver" },
    { ruta: "/invoiceItem", acceso: "conceptoCobro.ver" },
    { ruta: "/descuentocatalogo", acceso: "descuento.listar" },
    { ruta: "/objetivocatalogo", acceso: "evaluacion.ver" },
    { ruta: "/participanteobjetivo", acceso: "evaluacion.ver" },

    { ruta: "/puntuacion/edit", acceso: "puntuacion.editar" },
    { ruta: "/puntuacion", acceso: "puntuacion.ver" },

    // Los parametros del sistema son de la direccion: solo el Administrador
    // y el Director los tienen (acceso configuracion.ver).
    { ruta: "/configuracion/edit", acceso: "configuracion.editar" },
    { ruta: "/configuracion", acceso: "configuracion.ver" },

    // La bitacora de cambios: de solo lectura, y solo para quien puede ver
    // la seguridad.
    { ruta: "/bitacora", acceso: "bitacora.ver" },

    { ruta: "/security/user", acceso: "usuario.ver" },
    { ruta: "/security/role", acceso: "rol.ver" },
    { ruta: "/security/access", acceso: "seguridad.usuario" },
    { ruta: "/authorization", acceso: "autorizacion.ver" },
];

// Una ruta puede declarar varios accesos: basta con tener uno de ellos.
// Asi el terapeuta entra a la ventana de plan por su propio acceso, y la
// supervision por el general.
const accesoDeRuta = (pathname) => {
    const encontrada = RUTAS_POR_ACCESO.find((item) => item.exacta
        ? pathname === item.ruta
        : pathname === item.ruta || pathname.startsWith(item.ruta));

    if (!encontrada) return null;

    return Array.isArray(encontrada.acceso) ? encontrada.acceso : [encontrada.acceso];
};

const RutaConAcceso = ({ children }) => {
    const location = useLocation();
    const accesos = useSelector((state) => state.seguridad?.accesos) || [];
    const roles = useSelector((state) => state.seguridad?.roles) || [];
    const perfilCargado = useSelector((state) => state.seguridad?.perfilCargado) || false;
    const seguridadActiva = useSelector((state) => state.seguridad?.seguridadActiva) || "N";
    const usuario = obtenerUsuario();

    // Restricción específica para las opciones de terapeuta:
    // Solo permitidas para usuarios con rol Terapeuta.
    if (location.pathname.startsWith("/therapyplanterapist") || location.pathname.startsWith("/sesionterapeuta")) {
        if (perfilCargado && !esRolTerapeuta(roles)) {
            return <Navigate to="/" replace />;
        }
    }

    // Restricción específica para la Ficha de Matrícula:
    // Solo permitida para Secretaria, Administrador, Director y Monitoreo.
    if (location.pathname.startsWith("/reporte/ficha-participante")) {
        if (!puedeAccederFichaIngreso(usuario, roles)) {
            return <Navigate to="/reporte" replace />;
        }
    }

    // Restricción específica para Kardex e Inventario y Caja Chica:
    // Solo permitidas para Secretaria, Administrador y Director.
    if (location.pathname.startsWith("/inventario") || location.pathname.startsWith("/cajachica")) {
        if (!puedeAccederKardexYCaja(usuario, roles)) {
            return <Navigate to="/" replace />;
        }
    }

    // Sin informacion de seguridad todavia no se bloquea: recien se esta
    // cargando y no se quiere expulsar al usuario antes de tiempo.
    if (seguridadActiva !== "S" || accesos.length === 0) return children;

    const acceso = accesoDeRuta(location.pathname);

    // Las rutas sin acceso declarado (inicio, contacto, perfil) son libres.
    if (!acceso || acceso.some((codigo) => accesos.includes(codigo))) return children;

    return <Navigate to="/" replace />;
}

export default RutaConAcceso;